import * as Sentry from '@sentry/nextjs'
import { isPrd } from '@shared/util/env'

const AF_WEB_APP_ID = process.env.NEXT_PUBLIC_APPSFLYER_WEB_APP_ID

/**
 * Initialize AppsFlyer PBA SDK via proxy to bypass ad blockers.
 * This replaces the GTM tag "AppsFlyer | All Pages".
 *
 * The SDK is loaded from /a/af-sdk/ which is proxied via Next.js rewrites
 * to websdk.appsflyer.com, making it appear as first-party traffic.
 */
export const initAppsFlyerPBA = (): void => {
    if (typeof window === 'undefined') {
        return
    }

    if (!AF_WEB_APP_ID) {
        if (!isPrd()) {
            console.warn(
                'AppsFlyer Web App ID is missing! Check NEXT_PUBLIC_APPSFLYER_WEB_APP_ID in your .env file.',
            )
        }
        Sentry.captureMessage('AppsFlyer Web App ID is missing', {
            level: 'warning',
            tags: {
                errorType: 'missing_config',
                analytics: 'appsflyer',
            },
        })
        return
    }

    try {
        // This is the same initialization from GTM,
        // but loading from our proxy instead of websdk.appsflyer.com
        ;(function (
            t: Window & typeof globalThis,
            e: Document,
            n: string,
            _s: number,
            a: string,
            _c: string,
            i: string,
        ) {
            ;(t as any).AppsFlyerSdkObject = a
            ;(t as any).AF =
                (t as any).AF ||
                function () {
                    ;((t as any).AF.q = (t as any).AF.q || []).push(
                        [Date.now()].concat(
                            // eslint-disable-next-line prefer-rest-params
                            Array.prototype.slice.call(arguments),
                        ),
                    )
                }
            ;(t as any).AF.id = (t as any).AF.id || i
            ;(t as any).AF.plugins = {}
            ;(t as any).AF.PBA = {} // Pre-initialize PBA namespace to prevent race condition (fixes WEB-B4B)
            const o = e.createElement(n) as HTMLScriptElement
            const p = e.getElementsByTagName(n)[0]
            o.async = true
            // Load from proxy instead of websdk.appsflyer.com
            o.src = `/a/af-sdk/?af_id=${i}`
            p?.parentNode?.insertBefore(o, p)
        })(window, document, 'script', 0, 'AF', 'pba', AF_WEB_APP_ID)

        if (!isPrd()) {
            console.debug('AppsFlyer PBA SDK initialized via proxy')
        }
    } catch (error) {
        Sentry.captureException(error, {
            tags: {
                errorType: 'init_failed',
                analytics: 'appsflyer',
            },
        })
        if (!isPrd()) {
            console.error('Failed to initialize AppsFlyer PBA SDK:', error)
        }
    }
}
